import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAdminStore } from '../stores/adminStore';
import { Button } from '../components/common/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { ServiceCategory } from '../types/service';
import ServiceCategoryModalInfo from '../components/admin/modals/ServiceCategoryModalInfo';
import ServiceCategoryTable from '../components/admin/ServiceCategoryTable';
import AddServiceCategoryModal from '../components/admin/modals/AddServiceCategoryModal';

const ServiceCategoryList: React.FC = () => {
  const { t } = useTranslation(['common', 'admin', 'jobs']);

  const location = useLocation();
  const navigate = useNavigate();

  const { serviceCategories, fetchServiceCategories, fetchMoreServiceCategories, hasMore } =
    useAdminStore();

  const [selectedServiceCategory, setSelectedServiceCategory] = useState<ServiceCategory | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const state = (location.state || {}) as { selected_id?: number };
    if (state?.selected_id) {
      const selectedId = Number(state.selected_id);
      const found = serviceCategories.find(s => s.id === selectedId);

      if (found) {
        setSelectedServiceCategory(found);
        setIsModalOpen(true);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, serviceCategories, navigate]);

  useEffect(() => {
    setLoading(true);
    if (!serviceCategories.length) {
      fetchServiceCategories({ offsetCount: 0 })
        .catch(() => {
          toast.error(t('generic_error', { ns: 'common' }));
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [serviceCategories.length, fetchServiceCategories, t]);

  const handleRowClick = (serviceCategory: ServiceCategory) => {
    setSelectedServiceCategory(serviceCategory);
    setIsModalOpen(true);
  };

  const handleAddClick = () => {
    setSelectedServiceCategory(null);
    setOpenAddModal(true);
  };

  const handleEditClick = (serviceCategory: ServiceCategory) => {
    setSelectedServiceCategory(serviceCategory);
    setIsModalOpen(false);
    setOpenEditModal(true);
  };

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      await fetchMoreServiceCategories();
    } catch {
      toast.error(t('generic_error', { ns: 'common' }));
    } finally {
      setLoading(false);
    }
  };

  const visibleCategories = useMemo(
    () => serviceCategories.filter(category => !category.deleted),
    [serviceCategories]
  );
  const pendingCategories = useMemo(
    () => visibleCategories.filter(category => !category.validated),
    [visibleCategories]
  );

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('breadcrumbs.services', { ns: 'common', defaultValue: 'Services' })}
        </h1>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm dark:border-white/[0.08] dark:bg-white/[0.03] transition-all">
        <div className="flex flex-wrap gap-3 items-center justify-end px-4 py-3 border-b border-gray-300 bg-gray-50">
          <Button
            type="button"
            variant="transparent-with-border"
            className="w-auto w-full text-left transition duration-300 ease-in-out hover:bg-gray-200 md:ml-auto md:px-8"
            onClick={handleAddClick}
          >
            {t('add_new_service', { ns: 'common' })}
          </Button>
        </div>

        <div className="border-b border-gray-200">
          <div className="px-4 py-4">
            <h2 className="text-xl font-bold text-secondary">
              {t('pendingServicesTitle', {
                ns: 'admin',
                defaultValue: 'Nouveaux services à approuver',
              })}{' '}
              <span className="text-sm font-semibold text-gray-500">
                ({pendingCategories.length})
              </span>
            </h2>
            {pendingCategories.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">
                {t('pendingServicesEmpty', {
                  ns: 'admin',
                  defaultValue: 'Aucun nouveau service à approuver.',
                })}
              </p>
            ) : (
              <div className="mt-4">
                <ServiceCategoryTable
                  serviceCategories={pendingCategories}
                  onRowClick={handleRowClick}
                />
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="px-4 py-4">
            <h2 className="mb-4 text-xl font-bold text-secondary">
              {t('allServicesTitle', { ns: 'admin', defaultValue: 'Tous les services' })}
            </h2>
          </div>
          <ServiceCategoryTable serviceCategories={visibleCategories} onRowClick={handleRowClick} />
        </div>

        {hasMore?.serviceCategories && (
          <div className="py-4 flex justify-center">
            <Button className="px-8" onClick={handleLoadMore} disabled={loading}>
              {loading
                ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
                : t('loadMore', { ns: 'common', defaultValue: 'Charger plus' })}
            </Button>
          </div>
        )}
      </div>
      {isModalOpen && (
        <ServiceCategoryModalInfo
          serviceCategory={selectedServiceCategory}
          onClose={() => setIsModalOpen(false)}
          onEdit={handleEditClick}
        />
      )}

      {openAddModal && (
        <AddServiceCategoryModal serviceCategory={null} onClose={() => setOpenAddModal(false)} />
      )}

      {openEditModal && selectedServiceCategory && (
        <AddServiceCategoryModal
          serviceCategory={selectedServiceCategory}
          onClose={() => setOpenEditModal(false)}
        />
      )}
    </>
  );
};

export default ServiceCategoryList;
